import * as React from 'react';

import * as classNames from 'classnames';
import * as moment from 'moment';
import { ConnectedProps, connect } from 'react-redux';
import { createSelector } from 'reselect';

import { Alert, Button, ButtonContainer, CircularProgress, Modal, ModalFooter, Spacer, Tooltip, Typography } from '@emburse/ui-core';

import { ICardsGatewayIssuingModalValues, init, reset, submit } from '@Actions/cardsGatewayIssuingModalActions';
import { IAppStateContainer } from '@Reducers/index';

import { CardIssuingApiService } from '@ClientApi/cardIssuing';
import { CardRequestAPI } from '@ClientApi/cardRequestApi';
import { PendoEvents, PendoService } from '@ClientApi/pendo';
import { IUserExtension } from '@ClientModels/userExtension';
import { CARD_ISSUING_ERRORS, INSTITUTION_TYPE } from '@Shared/constants';
import { ADMIN_ROLE, MANAGER_ROLE } from '@Shared/constants/user_profile_constants';
import { parseCardsGatewayError } from '@Shared/helpers/cardsGatewayErrorFormatter';
import { canManagerIssueRequestedCard } from '@Shared/helpers/validators';
import {
  AccountCapabilities,
  AccountCapabilitiesCardAllowanceIntervalEnum,
  AccountConnectionFeedBackendEnum,
  CardType,
  CardsGatewayIssuingModes,
  CreateCardType,
  ShippingOption,
  ShippingOptionServiceEnum
} from '@Shared/interfaces/CardsGatewayInterfaces';
import {
  ICardRequestModel,
  ICardTemplateModel,
  IExpenseTagBucketModel,
  IExpenseTagToCardRequestModel,
  IExpenseTagToLinkedAccountModel,
  ILinkedAccountModel,
  IUserProfileModel
} from '@Shared/models';

import isEmail from 'validator/lib/isEmail';

import { isManagerFlagCheck } from '../../../utils/userProfileRoleHelper';
import { AddressForm } from './Forms/AddressForm';
import { CardTypeForm } from './Forms/CardTypeForm';
import { ExpenseFieldsForm } from './Forms/ExpenseFieldsForm';
import { IssuingDetailsForm } from './Forms/IssuingDetailsForm';
import { ShippingMethodForm } from './Forms/ShippingMethodForm';
import { getAmexErrorsFromAmexGatewayValidationError } from './error_utils';

import * as style from './CardsGatewayIssuingModal.styl';

interface ICardsGatewayIssuingModalOwnProps {
  onSubmit(response?: any): void;

  closeModal(): void;

  // Component props
  capabilities: AccountCapabilities; // Object detailing what fields the card program actually supports
  parentLinkedAccountId: string;
  issuingMode?: CardsGatewayIssuingModes;
  linkedAccountId?: string; // Only defined if editing or duplicating a card
  institutionName?: string; // Only used in virtual card issuing
  companyId?: string;
  activeExpenseBucketsWithActiveTags?: IExpenseTagBucketModel[];
  cardRequest?: ICardRequestModel;
  showAsDrawer?: boolean;
  selectedCreateCardType?: CreateCardType;
  onApprove?: (response?: any, forwardToAdmin?: boolean) => void;
  onDeny?: (cardRequest: ICardRequestModel) => void;
  cardTemplate?: ICardTemplateModel;
}

interface ICardsGatewayIssuingModalState extends ICardsGatewayIssuingModalValues {
  // General
  errorMessage?: string | React.ReactElement | null;
  needsUserUpdate?: boolean;
  canIssueCard?: boolean;
  isEligibleForExpeditedShipping?: boolean;
  showShippingMethodForm?: boolean;
}

function isMultiSelectAllowed(state: ICardsGatewayIssuingModalState, props: ICardsGatewayIssuingModalProps) {
  if (state.cardType === CardType.Physical || props.cardRequest) {
    return false;
  }

  if (props.linkedAccountId) {
    if (props.issuingMode === 'duplicate') {
      return true;
    }

    if (props.issuingMode === 'new') {
      return true;
    }
    return false;
  }

  return true;
}

export class CardsGatewayIssuingModal extends React.Component<ICardsGatewayIssuingModalProps, ICardsGatewayIssuingModalState> {
  constructor(props: ICardsGatewayIssuingModalProps) {
    super(props);
    let stateParams = {
      // Values
      allowanceInterval: AccountCapabilitiesCardAllowanceIntervalEnum.Continuous.toLowerCase(),
      allowanceMerchantCategories: [] as any[],
      allowanceMerchantGroupCategories: [] as any[],
      allowanceVendor: '',
      cardType: CardsGatewayIssuingModal.getDefaultCardType(props),
      selectedExpenseTagsByBucketId: {},
      textExpenseTagsByBucketId: {},
      cardRequestId: props.cardRequest?.id,
      cardRequestVersion: props.cardRequest?.version,
      shippingOptions: {
        Standard: [],
        Express: [],
        Priority: []
      },
      shippingRequireSignature: false
    } as ICardsGatewayIssuingModalState;

    if (props.selectedCreateCardType) {
      if (props.selectedCreateCardType === CardType.Physical) {
        stateParams.cardType = CardType.Physical;
      }
    }

    stateParams.enableMultiSelect = isMultiSelectAllowed(stateParams, props);

    const cardTemplate = props.cardTemplate;

    if (props.issuingMode === 'template' && cardTemplate) {
      const isVirtualCardForm = CardsGatewayIssuingModal.shouldBeVirtualCardForm(props.capabilities);

      stateParams.cardTemplateId = cardTemplate.id;
      stateParams.cardName = cardTemplate.purpose;
      stateParams.allowanceAmount = cardTemplate.budget;
      stateParams.allowanceInterval = cardTemplate.frequency;
      stateParams.allowanceStartDate = cardTemplate.start_date ? moment(cardTemplate.start_date) : null;
      stateParams.allowanceEndDate = cardTemplate.end_date ? moment(cardTemplate.end_date) : null;

      const merchantCategoryCodes = cardTemplate.category_codes ? cardTemplate.category_codes.split(',') : [];
      const cardCapabilities = isVirtualCardForm ? props.capabilities.virtualCard : props.capabilities.card;

      if (merchantCategoryCodes.length <= Number(cardCapabilities?.allowance?.supportedMerchantGroupRestrictions)) {
        stateParams.allowanceMerchantGroupCategories = merchantCategoryCodes;
      }
    }

    this.state = stateParams;
    this.isManager = isManagerFlagCheck(this.props.userProfile);
  }

  async getIsCardIssuingSupported() {
    let canIssueCard = false;

    if (this.props.userProfile?.role === ADMIN_ROLE) {
      return true;
    }

    if (this.props.userProfile?.role === MANAGER_ROLE) {
      const company = window.ORG;
      const canManagersIssueCards = company.is_manager_card_issuing_enabled;

      if (!canManagersIssueCards) {
        return false;
      }

      // if managers can issue cards and this isn't preloading a card request, allow managers to issue card
      if (!this.state.cardRequestId) {
        return true;
      }
      const cardRequestJourney = await CardRequestAPI.getJourney(this.state.cardRequestId);
      const hasPendingApproverSteps = cardRequestJourney.some((step) => step.type === 'manager_approval' && step.time_period === 'future');

      canIssueCard = !hasPendingApproverSteps;
      return canIssueCard;
    }

    return false;
  }

  static getSelectedExpenseTagsFromInitialValue(
    activeExpenseBucketsWithActiveTags: IExpenseTagBucketModel[],
    expense_tags_to_linked_account: IExpenseTagToLinkedAccountModel[] | IExpenseTagToCardRequestModel[]
  ) {
    if (expense_tags_to_linked_account) {
      const selectedExpenseTagsByBucketId: Record<string, string> = {};
      const textExpenseTagsByBucketId: Record<string, string> = {};

      expense_tags_to_linked_account.forEach((etlc: IExpenseTagToLinkedAccountModel | IExpenseTagToCardRequestModel) => {
        const bucket = activeExpenseBucketsWithActiveTags.find((etb) => etb.id === etlc.expense_tag_bucket_id);
        if (bucket?.input_type === 'text') {
          const tag = bucket.expense_tags.find((et) => et.id === etlc.expense_tag_id);
          textExpenseTagsByBucketId[bucket.id] = tag && tag.name;
        } else if (bucket?.input_type === 'dropdown') {
          selectedExpenseTagsByBucketId[bucket.id] = etlc.expense_tag_id;
        }
      });

      return {
        selectedExpenseTagsByBucketId: selectedExpenseTagsByBucketId,
        textExpenseTagsByBucketId: textExpenseTagsByBucketId
      };
    }
  }

  static getDerivedStateFromProps(props: ICardsGatewayIssuingModalProps, state: ICardsGatewayIssuingModalState) {
    const { issuingDetailsOriginalValues, parentCardOptions, capabilities, cardRequest, userProfile } = props;

    let stateParams: Partial<ICardsGatewayIssuingModalState> = {};

    const isVirtualCardForm = CardsGatewayIssuingModal.shouldBeVirtualCardForm(capabilities);

    if (Boolean(issuingDetailsOriginalValues) && state.id !== issuingDetailsOriginalValues.id) {
      stateParams.id = issuingDetailsOriginalValues.id;
      stateParams.assigneeUserId = issuingDetailsOriginalValues.user_id;
      stateParams.parentCardId = issuingDetailsOriginalValues.parent_card && issuingDetailsOriginalValues.parent_card.id;
      stateParams.cardName = issuingDetailsOriginalValues.description;

      if (isVirtualCardForm) {
        stateParams.cardType = CardType.Virtual;
      } else {
        if (cardRequest) {
          stateParams.cardType = CardsGatewayIssuingModal.getDefaultCardType(props);
        } else {
          stateParams.cardType = issuingDetailsOriginalValues.is_ghost ? CardType.Ghost : CardType.Physical;
        }
      }

      const allowanceObject = issuingDetailsOriginalValues.allowance;
      stateParams.allowanceId = allowanceObject.id;
      stateParams.allowanceAmount = allowanceObject.amount;
      stateParams.allowanceInterval = allowanceObject.interval;
      stateParams.allowanceMerchantCategories = allowanceObject.scope;
      stateParams.allowanceMerchantGroupCategories = allowanceObject.group_scope;
      stateParams.allowanceVendor = allowanceObject.vendor;
      stateParams.allowanceDailyLimit = allowanceObject.daily_limit;
      stateParams.allowanceStartDate = allowanceObject.start_time && moment(allowanceObject.start_time);
      stateParams.allowanceEndDate = allowanceObject.end_time && moment(allowanceObject.end_time);

      if (props.issuingMode === 'duplicate') {
        if (stateParams.allowanceStartDate) {
          const delta = stateParams.allowanceStartDate.diff(moment(issuingDetailsOriginalValues.created_at), 'days');
          stateParams.allowanceStartDate = moment().add(delta, 'days');
        }

        if (stateParams.allowanceEndDate) {
          const delta = stateParams.allowanceEndDate.diff(moment(issuingDetailsOriginalValues.created_at), 'days');
          stateParams.allowanceEndDate = moment().add(delta, 'days');
        }
      }

      stateParams.allowanceTransactionLimit = allowanceObject.transaction_limit;

      stateParams.selectedCategoryId = issuingDetailsOriginalValues.default_category_id;
      const expenseTags = CardsGatewayIssuingModal.getSelectedExpenseTagsFromInitialValue(
        props.activeExpenseBucketsWithActiveTags,
        issuingDetailsOriginalValues.expense_tags_to_linked_account
      );

      if (expenseTags) {
        stateParams.selectedExpenseTagsByBucketId = expenseTags.selectedExpenseTagsByBucketId;
        stateParams.textExpenseTagsByBucketId = expenseTags.textExpenseTagsByBucketId;
      }

      if (cardRequest) {
        stateParams.addressRecipient = cardRequest.recipient_name;
        stateParams.addressStreet = cardRequest.street_address;
        stateParams.addressCity = cardRequest.address_city;
        stateParams.addressState = cardRequest.address_state;
        stateParams.addressZipCode = cardRequest.postal_code;
      }
    }

    // If there's only 1 parent card option, we'll disable the dropdown, so we should auto-select that 1 option
    if (parentCardOptions.length === 1) {
      stateParams.parentCardId = parentCardOptions[0].id;
    }

    if (cardRequest && !CardsGatewayIssuingModal.canIssueFromCardRequest(cardRequest, userProfile)) {
      stateParams.errorMessage = 'This card has already been issued. Therefore you cannot issue it again.';
    }

    return stateParams;
  }

  isManager: boolean | undefined;

  async componentDidMount() {
    this.props.init(this.isVirtualCardForm(), this.props.parentLinkedAccountId, this.props.linkedAccountId, this.props.cardRequest);
    const canIssueCard = await this.getIsCardIssuingSupported();

    this._initializeShippingOptions(
      this.state,
      this.props.cardPrograms?.find(({ id }) => id === this.props.parentLinkedAccountId)
    );

    this.setState({
      canIssueCard: canIssueCard
    });
  }

  // Usually, we prefer showing the form in virtual card mode, then ghost card mode, then physical card mode
  // However, if we're issuing from a physical card request, we want to show the physical card form
  static getDefaultCardType(props: ICardsGatewayIssuingModalOwnProps) {
    const { capabilities } = props;
    if (props.cardRequest?.recipient_name && capabilities.issuing?.physical) {
      return CardType.Physical;
    } else if (capabilities.issuing?.virtual) {
      return CardType.Virtual;
    } else if (capabilities.issuing?.ghost) {
      return CardType.Ghost;
    } else if (capabilities.issuing?.physical) {
      return CardType.Physical;
    }
    throw new Error('Cannot issue cards');
  }

  get moreThanOneUserSelected() {
    return this.state.enableMultiSelect && this.state.assigneeUserIds && this.state.assigneeUserIds.length > 1;
  }

  static shouldBeVirtualCardForm = (capabilities?: AccountCapabilities) => capabilities?.issuing?.virtual || false;

  isVirtualCardForm = () => CardsGatewayIssuingModal.shouldBeVirtualCardForm(this.props.capabilities);

  isInEditMode = () => {
    if (typeof this.props.issuingMode === 'undefined') {
      return Boolean(this.props.linkedAccountId);
    }
    return this.props.issuingMode === 'edit';
  };

  cardCapabilities = () => (this.isVirtualCardForm() ? this.props.capabilities?.virtualCard : this.props.capabilities?.card);

  static canIssueFromCardRequest = (cardRequest: ICardRequestModel, userProfile: IUserProfileModel) => {
    const isAssignedManager = cardRequest.manager_id === userProfile.id;
    const isAdmin = userProfile.role === ADMIN_ROLE;
    return (
      ['new', 'pending_final_approval', 'pending_invite'].includes(cardRequest.status) ||
      // Admin that is also the manager should be able to issue card without approve step
      (cardRequest.status === 'pending_manager_review' && (isAssignedManager || isAdmin))
    );
  };

  _initializeShippingOptions = (stateParams: ICardsGatewayIssuingModalState, program?: ILinkedAccountModel) => {
    const isEligibleForExpeditedShipping =
      stateParams.cardType === CardType.Physical &&
      ['emburse', 'gringotts'].some((institution) => program?.institution_type === institution); // expedited shipping is only available for emburse cards and gringotts

    let groupedShippingOptions: Record<ShippingOptionServiceEnum, ShippingOption[]> = {
      Standard: [],
      Express: [],
      Priority: []
    };

    if (isEligibleForExpeditedShipping) {
      CardIssuingApiService.getShippingOptions(this.props.parentLinkedAccountId)
        .then((shippingOptions) => {
          // Only supports US, non-international and individual shipping options
          const filtertedShippingOptions = shippingOptions.filter(
            (option) =>
              option.originCountry === 'us' &&
              option.destinationWithinOrigin &&
              [ShippingOptionServiceEnum.Standard, ShippingOptionServiceEnum.Express].some((service) => service === option.service)
          );

          // groupd shippingOptions by service
          groupedShippingOptions = filtertedShippingOptions.reduce((acc, option) => {
            if (!acc[option.service]) {
              acc[option.service] = [];
            }
            acc[option.service].push(option);
            return acc;
          }, {} as Record<string, ShippingOption[]>);

          this.setState({
            shippingService: 'Standard',
            isEligibleForExpeditedShipping: isEligibleForExpeditedShipping,
            shippingOptions: groupedShippingOptions, // We only want to support Standard and Express for now
            showShippingMethodForm: Boolean(
              groupedShippingOptions?.Express &&
                groupedShippingOptions?.Express.length > 0 &&
                groupedShippingOptions?.Standard &&
                groupedShippingOptions?.Standard.length > 0
            )
          });
        })
        .catch(function () {
          // fail silently
        });
    }
  };

  onChange = (updatedState: ICardsGatewayIssuingModalValues) => {
    let hasUserChanged =
      updatedState.assigneeUserIds !== this.state.assigneeUserIds ||
      updatedState.assigneeUserId !== this.state.assigneeUserId ||
      updatedState.assigneeEmail !== this.state.assigneeEmail;

    if (updatedState.cardType === CardType.Physical) {
      updatedState.enableMultiSelect = false;
      updatedState.assigneeUserIds = [];
      hasUserChanged = true;
    }

    if (updatedState.cardType === CardType.Ghost || updatedState.cardType === CardType.Virtual) {
      if (isMultiSelectAllowed(this.state, this.props)) {
        updatedState.enableMultiSelect = true;
        hasUserChanged = true;
      }
    }

    if (this.state.enableMultiSelect && (updatedState.assigneeUserId || updatedState.assigneeEmail)) {
      const assigneeUserIds = this.state.assigneeUserIds || [];
      const existingSelection = assigneeUserIds?.find((x) => x === updatedState.assigneeEmail || x === updatedState.assigneeUserId);

      if (existingSelection) {
        updatedState.assigneeUserIds = assigneeUserIds.filter((x) => x !== existingSelection);
      } else {
        assigneeUserIds.push(updatedState.assigneeUserId || updatedState.assigneeEmail!);
        updatedState.assigneeUserIds = assigneeUserIds;
      }
    }

    this._initializeShippingOptions(
      updatedState,
      this.props.cardPrograms?.find(({ id }) => id === this.props.parentLinkedAccountId)
    );

    this.setState({
      needsUserUpdate: hasUserChanged ? false : this.state.needsUserUpdate,
      ...updatedState
    });
  };

  onClose = () => {
    this.props.reset();
    this.props.closeModal();
  };

  onDeny = () => {
    const carRequest = this.props.cardRequest;
    const onDeny = this.props.onDeny;

    if (carRequest && onDeny) {
      onDeny(carRequest);
    }
  };

  validateFields() {
    const {
      allowanceAmount,
      allowanceTransactionLimit,
      allowanceDailyLimit,
      allowanceMerchantCategories,
      allowanceMerchantGroupCategories,
      allowanceVendor,
      allowanceEndDate,
      cardName,
      assigneeUserIds,
      assigneeUserId,
      assigneeEmail,
      cardType,
      addressRecipient,
      addressStreet,
      addressCity,
      addressState,
      addressZipCode,
      addressPhoneNumber,
      parentCardId,
      firstName,
      lastName
    } = this.state;
    const capabilities = this.cardCapabilities();
    const program = this.props.cardPrograms?.find(({ id }) => id === this.props.parentLinkedAccountId);

    const isNewUser = !assigneeUserId && assigneeEmail;
    const isInvitedUser = assigneeUserId && this.props.usersById[assigneeUserId]?.is_invited;
    const firstAndLastNameRequired =
      window.FEATURES.consolidated_invite_issuing && (isNewUser || isInvitedUser) && cardType === CardType.Physical;

    if (this.state.enableMultiSelect) {
      if (!assigneeUserIds || assigneeUserIds.length === 0) {
        return 'Employee is required';
      }
    } else {
      if (!assigneeUserId && (!assigneeEmail || !isEmail(assigneeEmail))) {
        return 'Employee is required';
      }
    }
    if (assigneeUserId && this.props.usersById[assigneeUserId] && !this.props.usersById[assigneeUserId].phone_number) {
      if (program?.institution_type === AccountConnectionFeedBackendEnum.AmexCpg) {
        this.setState({ needsUserUpdate: true });
        return "Employee doesn't have a phone number - card cannot be issued";
      }
    }
    if (!cardName && !firstAndLastNameRequired) {
      return `Card ${this.isVirtualCardForm() ? 'Purpose' : 'Name'} is required`;
    }
    if (!firstName && firstAndLastNameRequired) {
      return 'First Name is required';
    }
    if (!lastName && firstAndLastNameRequired) {
      return 'Last Name is required';
    }
    if (!allowanceAmount || allowanceAmount <= 0) {
      return CARD_ISSUING_ERRORS.BUDGET_MUST_BE_POSITIVE;
    }
    if (typeof allowanceDailyLimit === 'number' && allowanceDailyLimit <= 0) {
      return 'Daily Budget must be greater than $0.00';
    }
    if (typeof allowanceDailyLimit === 'number' && allowanceDailyLimit > allowanceAmount) {
      return 'Daily Budget cannot exceed Total Budget';
    }
    if (
      typeof allowanceTransactionLimit === 'number' &&
      typeof allowanceDailyLimit === 'number' &&
      allowanceTransactionLimit > allowanceDailyLimit
    ) {
      return 'Transaction Limit cannot exceed Daily Limit';
    }
    if (typeof allowanceTransactionLimit === 'number' && allowanceTransactionLimit > allowanceAmount) {
      return 'Transaction Limit cannot exceed Total Budget';
    }
    if (allowanceVendor && allowanceVendor.length > 0 && capabilities?.allowance?.allowedVendor === false) {
      return 'Vendor Restrictions are not allowed for this card program.';
    }
    if (allowanceMerchantCategories?.length > capabilities?.allowance?.supportedMerchantRestrictions) {
      return `A maximum of ${capabilities?.allowance?.supportedMerchantRestrictions} Merchant Category Codes can be selected.`;
    }
    if (allowanceMerchantGroupCategories?.length > capabilities?.allowance?.supportedMerchantGroupRestrictions) {
      return `A maximum of ${capabilities?.allowance?.supportedMerchantGroupRestrictions} Merchant Group Category Codes can be selected.`;
    }
    if (allowanceEndDate?.isSameOrBefore(new Date(), 'day')) {
      return 'The date cannot be in the past.';
    }

    const addressIsIncomplete = !addressRecipient || !addressStreet || !addressCity || !addressState || !addressZipCode;
    if (!this.isInEditMode() && cardType === CardType.Physical && addressIsIncomplete) {
      return 'Shipping Address is required for Plastic Cards';
    }

    if (program?.institution_type === INSTITUTION_TYPE.EMBURSE && addressStreet && addressStreet.length > 50) {
      return 'Street Address must be 50 characters or less.';
    }

    if (cardType === CardType.Virtual && !parentCardId) {
      return 'Parent Card is required for Virtual Cards.';
    }

    if (this.isManager && !assigneeUserId) {
      return 'Managers Cannot Invite Users.';
    }

    if (addressPhoneNumber && addressPhoneNumber.length !== 10) {
      return 'Phone number must be 10 digits.';
    }

    return null;
  }

  getFieldsForForwardingRequest() {
    const seleectedExpenseTagIds = Object.values(this.state.selectedExpenseTagsByBucketId);
    const textExpenseTagsByBucketId = this.state.textExpenseTagsByBucketId;

    return {
      ...this.props.cardRequest,
      purpose: this.state.cardName || '',
      budget: this.state.allowanceAmount || 0,
      frequency: this.state.allowanceInterval as any /** this needs a type */,
      default_category_id: this.state.selectedCategoryId /** double check the ICardRequestModel, lot of these props are optional */,
      expense_tag_ids: seleectedExpenseTagIds,
      text_expense_tags: textExpenseTagsByBucketId
    };
  }

  approveCardRequest = () => {
    const { onApprove } = this.props;
    if (!onApprove) {
      throw new Error('onApprove is not defined');
    }

    if (!this.props.cardRequest) {
      throw new Error('card request is undefiend');
    }

    const updatedRequest = this.getFieldsForForwardingRequest();
    onApprove(updatedRequest, true);
    this.onClose();
  };

  submitCard = () => {
    const errorMessage = this.validateFields();
    this.setState({ errorMessage }); // Will reset an old errorMessage if there is no error.
    if (errorMessage) {
      return; // Don't submit on validation error
    }

    const { parentLinkedAccountId, companyId, issuingDetailsOriginalValues, onSubmit } = this.props;
    let linkedAccountId = this.props.linkedAccountId;
    if (!this.isInEditMode()) {
      linkedAccountId = undefined; // unset for duplicating a card
    }
    const {
      assigneeUserIds,
      assigneeUserId,
      assigneeEmail,
      parentCardId,
      cardName,
      allowanceId,
      allowanceDailyLimit,
      allowanceMerchantCategories,
      allowanceMerchantGroupCategories,
      allowanceVendor,
      allowanceAmount,
      allowanceInterval,
      allowanceStartDate,
      allowanceEndDate,
      allowanceTransactionLimit,
      cardType,
      addressRecipient,
      addressStreet,
      addressCity,
      addressState,
      addressZipCode,
      addressPhoneNumber,
      selectedCategoryId,
      selectedExpenseTagsByBucketId,
      textExpenseTagsByBucketId,
      cardRequestId,
      cardRequestVersion,
      firstName,
      lastName,
      shippingService,
      shippingRequireSignature,
      cardTemplateId
    } = this.state;

    this.props.submit(
      parentLinkedAccountId,
      {
        assigneeUserIds,
        assigneeUserId,
        assigneeEmail,
        parentCardId,
        cardName,
        allowanceId,
        allowanceDailyLimit,
        allowanceMerchantCategories,
        allowanceMerchantGroupCategories,
        allowanceVendor,
        allowanceAmount,
        allowanceInterval,
        allowanceStartDate,
        allowanceEndDate,
        allowanceTransactionLimit,
        cardType,
        addressRecipient,
        addressStreet,
        addressCity,
        addressState,
        addressZipCode,
        addressPhoneNumber,
        selectedCategoryId: cardType === CardType.Physical || this.moreThanOneUserSelected ? null : selectedCategoryId,
        selectedExpenseTagsByBucketId: cardType === CardType.Physical || this.moreThanOneUserSelected ? {} : selectedExpenseTagsByBucketId,
        textExpenseTagsByBucketId: cardType === CardType.Physical || this.moreThanOneUserSelected ? {} : textExpenseTagsByBucketId,
        cardRequestId,
        cardRequestVersion,
        firstName,
        lastName,
        shippingService,
        shippingRequireSignature,
        cardTemplateId
      },
      linkedAccountId,
      companyId,
      issuingDetailsOriginalValues,
      this.cardCapabilities(),
      (response) => {
        // only push event when we first issue card not on update
        if (!this.isInEditMode()) {
          PendoService.trackEvent(PendoEvents.CARD_ISSUED, {
            issued_to: assigneeUserId
          });
        }
        onSubmit(response);
      }
    );
  };

  tryParseAndReturnFormattedAMEXError(serverError: string) {
    try {
      const errors = getAmexErrorsFromAmexGatewayValidationError(serverError);
      if (!errors?.length) {
        return null;
      }

      const cardErrors = errors.map((err, index) => {
        return (
          <div key={index}>
            <Typography className="underlineText">Error Details:</Typography>
            <Typography>Error code: {err.code}</Typography>
            <Typography>Error message: {err.detail}</Typography>
          </div>
        );
      });

      return (
        <div>
          <Typography>
            This card cannot be issued at the moment - there was a problem during the verification of the card holder information.
          </Typography>
          <Spacer size={5} />
          {cardErrors}
        </div>
      );
    } catch (err) {
      // ignore errors - it was not AMEX Json error
    }
  }

  renderServerError(error?: string) {
    const errors = parseCardsGatewayError(error);

    if (errors.length === 0) {
      return;
    }

    const cardErrors = errors.map((err, index) => {
      return (
        <li key={`${err}-${index}`}>
          <Typography>{err}</Typography>
        </li>
      );
    });

    return (
      <div>
        <Typography>The card could not be created (or updated) due to the following reason(s):</Typography>
        <ul style={{ paddingLeft: '20px' }}>{cardErrors}</ul>
      </div>
    );
  }

  getOnlyOneUserInMultiSelect() {
    if (this.state.enableMultiSelect && this.state.assigneeUserIds?.length === 1) {
      return this.state.assigneeUserIds[0];
    }

    return undefined;
  }

  getAssignedUserId() {
    const userIdOrEmail = this.getOnlyOneUserInMultiSelect();
    if (userIdOrEmail) {
      if (userIdOrEmail.indexOf('@') > -1) {
        return undefined;
      }
      return userIdOrEmail;
    }
    return this.state.assigneeUserId;
  }

  getAssignedUserEmail() {
    const userIdOrEmail = this.getOnlyOneUserInMultiSelect();
    if (userIdOrEmail) {
      if (userIdOrEmail.indexOf('@') > -1) {
        return userIdOrEmail;
      }
      return undefined;
    }
    return this.state.assigneeEmail;
  }

  renderCardFormFields() {
    const {
      capabilities,
      linkedAccountId,
      institutionName,
      userOptions,
      usersById,
      parentCardOptions,
      parentCardsById,
      activeExpenseBucketsWithActiveTags,
      cardRequest,
      loading,
      showAsDrawer,
      selectedCreateCardType
    } = this.props;

    const allowCardTypeSelector = !selectedCreateCardType || !this.state.cardType;

    return (
      <>
        {allowCardTypeSelector && (
          <CardTypeForm
            linkedAccountId={linkedAccountId}
            capabilities={capabilities}
            cardType={this.state.cardType}
            onChange={this.onChange}
            userProfile={this.props.userProfile}
          />
        )}
        <IssuingDetailsForm
          cardRequestId={cardRequest?.id}
          capabilities={capabilities}
          linkedAccountId={linkedAccountId}
          institutionName={institutionName}
          userOptions={userOptions}
          usersById={usersById}
          parentCardOptions={parentCardOptions}
          parentCardsById={parentCardsById}
          values={{ ...this.state }}
          loading={loading}
          isInEditMode={this.isInEditMode()}
          onChange={this.onChange}
          showAsDrawer={showAsDrawer}
          userProfile={this.props.userProfile}
          multiple={this.state.enableMultiSelect || false}
        />
        {this.state.cardType !== CardType.Physical && (
          <>
            <Spacer size={16} />
            <ExpenseFieldsForm
              assigneeUserId={this.getAssignedUserId()}
              assigneeEmail={this.getAssignedUserEmail()}
              selectedCategoryId={this.moreThanOneUserSelected ? null : this.state.selectedCategoryId}
              selectedExpenseTagsByBucketId={this.moreThanOneUserSelected ? {} : this.state.selectedExpenseTagsByBucketId}
              textExpenseTagsByBucketId={this.state.textExpenseTagsByBucketId}
              activeExpenseBucketsWithActiveTags={activeExpenseBucketsWithActiveTags}
              onChange={this.onChange}
              disabled={this.moreThanOneUserSelected}
            />
          </>
        )}
        {this.state.cardType === CardType.Physical && !this.isInEditMode() && (
          <>
            <Spacer size={16} />
            <AddressForm
              recipient={this.state.addressRecipient}
              street={this.state.addressStreet}
              city={this.state.addressCity}
              state={this.state.addressState}
              zipCode={this.state.addressZipCode}
              phoneNumber={this.state.addressPhoneNumber}
              includePhoneNumberField={this.state.isEligibleForExpeditedShipping}
              onChange={this.onChange}
            />
            {this.state.showShippingMethodForm && this.state.shippingService && (
              <>
                <Spacer size={16} />
                <ShippingMethodForm
                  service={this.state.shippingService}
                  requireSignature={this.state.shippingRequireSignature}
                  shippingMethods={this.state.shippingOptions}
                  onChange={this.onChange}
                />
              </>
            )}
          </>
        )}
      </>
    );
  }

  renderModalBody() {
    const { loading, serverError, showAsDrawer } = this.props;
    const { errorMessage } = this.state;
    const isLoadingBody = loading === 'body';
    let errToRender = errorMessage || this.renderServerError(serverError);

    if (!errorMessage && serverError) {
      errToRender = this.tryParseAndReturnFormattedAMEXError(serverError) || errToRender;
    }
    return (
      <div className={classNames(style.global, 'card-issuing-modal')} style={{ margin: '8px 0' }}>
        {isLoadingBody && (
          <div className="flex ai-c jc-c" style={{ marginTop: showAsDrawer ? '150px' : 0 }} data-testid="Spinner">
            <CircularProgress />
          </div>
        )}
        <div
          className={classNames({ 'card-issuing-drawer': showAsDrawer, hidden: isLoadingBody })}
          data-testid="Card-Form-Fields-Container"
          hidden={isLoadingBody}
        >
          {errToRender && <Alert severity="error">{errToRender}</Alert>}
          {!isLoadingBody && this.renderCardFormFields()}
        </div>
      </div>
    );
  }

  renderApproveButton() {
    // this should target the updateCardStatus flow and pass in the correct payload
    if (this.state.canIssueCard) {
      return (
        <Button disabled={!this.props.cardRequest} variant="outlined" onClick={this.approveCardRequest}>
          Approve and Forward
        </Button>
      );
    }

    return (
      <Button disabled={!this.props.cardRequest} primary onClick={this.approveCardRequest}>
        Approve and Forward
      </Button>
    );
  }

  render() {
    const company = window.ORG;
    const cardRequest = this.props.cardRequest;
    const role = this.props.userProfile.role;
    const canIssueCard = this.state.canIssueCard;
    const showDenyButton = role === MANAGER_ROLE && cardRequest?.status === 'pending_manager_review';

    let cardIssuingWarning = ``;
    const managerIssueCardError = canManagerIssueRequestedCard(
      company,
      this.props.userProfile,
      this.state.cardType,
      this.state.allowanceAmount || this.props.cardRequest?.budget
    );

    if (this.props.userProfile.role === MANAGER_ROLE) {
      cardIssuingWarning = managerIssueCardError;
    } else {
      if (this.props.loading) {
        cardIssuingWarning = 'Card issuing in progress';
      } else {
        cardIssuingWarning = "You can't issue this card request";
      }
    }

    const disableCardIssuingButton =
      this.props.loading ||
      (this.props.cardRequest && !CardsGatewayIssuingModal.canIssueFromCardRequest(this.props.cardRequest, this.props.userProfile)) ||
      (this.props.userProfile.role === MANAGER_ROLE && managerIssueCardError) ||
      this.state.needsUserUpdate;

    const showApproveButton = Boolean(
      window.ORG.is_manager_card_issuing_enabled && this.props.userProfile.role === 'manager' && this.props.onApprove
    );

    if (this.props.showAsDrawer) {
      return (
        <>
          {this.renderModalBody()}
          <div style={{ marginTop: 'auto' }} />
          <ButtonContainer alignButtons="right" className="drawer-buttons">
            {showDenyButton && (
              <Button variant="outlined" onClick={this.onDeny} color="error">
                Deny
              </Button>
            )}
            {!showDenyButton && (
              <Button variant="outlined" onClick={this.onClose}>
                Cancel
              </Button>
            )}
            {showApproveButton ? this.renderApproveButton() : null}
            {canIssueCard && (
              <Tooltip title={disableCardIssuingButton ? cardIssuingWarning : ''}>
                <Button disabled={disableCardIssuingButton} primary onClick={this.submitCard}>
                  {this.isInEditMode() ? 'Update' : 'Issue Card'}
                </Button>
              </Tooltip>
            )}
          </ButtonContainer>
        </>
      );
    }

    return (
      <Modal
        open
        modalHeader={this.isInEditMode() ? 'Update' : 'Issue New Card'}
        titleonClose={this.onClose}
        modalFooter={
          <ModalFooter>
            <ButtonContainer alignButtons="right">
              {showDenyButton && (
                <Button variant="outlined" onClick={this.onDeny} color="error">
                  Deny
                </Button>
              )}
              {!showDenyButton && (
                <Button variant="outlined" onClick={this.onClose}>
                  Cancel
                </Button>
              )}
              {this.renderApproveButton()}
              {canIssueCard && (
                <Tooltip title={disableCardIssuingButton ? cardIssuingWarning : ''}>
                  <Button disabled={disableCardIssuingButton} primary onClick={this.submitCard}>
                    {this.isInEditMode() ? 'Update' : 'Issue Card'}
                  </Button>
                </Tooltip>
              )}
            </ButtonContainer>
          </ModalFooter>
        }
      >
        {this.renderModalBody()}
      </Modal>
    );
  }
}

const selectActiveUsers = createSelector(
  (state: IAppStateContainer) => state.users.usersActiveOrInvited,
  (usersActiveOrInvited: IUserExtension[]) => usersActiveOrInvited.filter((user) => user.user_profile.state === 'active')
);

const selectManagedUsers = createSelector(
  [(state: IAppStateContainer) => state.users.users, (state: IAppStateContainer) => state.currentUser.userProfile],
  (users, userProfile) => users.filter((user) => user.user_profile.manager_user_profile_id === userProfile.id)
);

const getUserOptions = (state: IAppStateContainer) => {
  const userProfile = state.currentUser.userProfile;
  if (userProfile.role === MANAGER_ROLE) {
    return selectManagedUsers(state);
  } else if (window.FEATURES.accelerated_card_issuing) {
    return state.users.usersActiveOrInvited;
  }
  return selectActiveUsers(state);
};

const mapStateToProps = (state: IAppStateContainer, ownProps: ICardsGatewayIssuingModalOwnProps) => ({
  userOptions: getUserOptions(state),
  ...ownProps,
  ...state.cardsGatewayIssuingModal,
  usersById: state.users.usersById,
  activeExpenseBucketsWithActiveTags: state.expenseBucketsWithTags.activeExpenseBucketsWithActiveTags,
  userProfile: state.currentUser.userProfile,
  cardPrograms: state.cards.programs
});

const mapDispatchToProps = {
  init,
  submit,
  reset
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export type ICardsGatewayIssuingModalProps = ConnectedProps<typeof connector>;
export const CardsGatewayIssuingModalConnected = connector(CardsGatewayIssuingModal);
